import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Globe, Plus, X } from 'lucide-react';
import { useBrowserPolicies } from '@/hooks/useBrowserPolicies';

const Browser: React.FC = () => {
  const navigate = useNavigate();
  const {
    policies,
    updatePolicy,
    addToWhitelist,
    removeFromWhitelist,
    addToBlacklist,
    removeFromBlacklist,
    isLoading
  } = useBrowserPolicies();

  const [newWhitelistSite, setNewWhitelistSite] = useState('');
  const [newBlacklistSite, setNewBlacklistSite] = useState('');

  const handlePolicyToggle = async (policyId: string, enabled: boolean) => {
    try {
      await updatePolicy(policyId, enabled);
    } catch (error) {
      console.error('Failed to update policy:', error);
    }
  };

  const handleAddWhitelist = async () => {
    if (newWhitelistSite.trim()) {
      try {
        await addToWhitelist(newWhitelistSite.trim());
        setNewWhitelistSite('');
      } catch (error) {
        console.error('Failed to add to whitelist:', error);
      }
    }
  };

  const handleAddBlacklist = async () => {
    if (newBlacklistSite.trim()) {
      try {
        await addToBlacklist(newBlacklistSite.trim());
        setNewBlacklistSite('');
      } catch (error) {
        console.error('Failed to add to blacklist:', error);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center gap-3 mb-6">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/settings')}
            className="p-2"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Browser Policies</h1>
            <p className="text-muted-foreground">Configure website restrictions</p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Browser Policies */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Content Policies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="text-center py-4">
                  <p className="text-muted-foreground">Loading policies...</p>
                </div>
              ) : (
                policies?.map((policy) => (
                  <div key={policy.id} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <h3 className="font-medium text-foreground">{policy.name}</h3>
                      {policy.description && (
                        <p className="text-sm text-muted-foreground">{policy.description}</p>
                      )}
                    </div>
                    <Switch
                      checked={policy.enabled || false}
                      onCheckedChange={(checked) => handlePolicyToggle(policy.id, checked)}
                    />
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Website Whitelist */}
          <Card>
            <CardHeader>
              <CardTitle>Allowed Websites</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add website to whitelist..."
                  value={newWhitelistSite}
                  onChange={(e) => setNewWhitelistSite(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-md bg-background text-foreground"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddWhitelist()}
                />
                <Button onClick={handleAddWhitelist} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {policies?.find(p => p.name === 'whitelist')?.websites?.map((site: string, index: number) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {site}
                    <button
                      onClick={() => removeFromWhitelist(site)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Website Blacklist */}
          <Card>
            <CardHeader>
              <CardTitle>Blocked Websites</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add website to blacklist..."
                  value={newBlacklistSite}
                  onChange={(e) => setNewBlacklistSite(e.target.value)}
                  className="flex-1 px-3 py-2 border rounded-md bg-background text-foreground"
                  onKeyPress={(e) => e.key === 'Enter' && handleAddBlacklist()}
                />
                <Button onClick={handleAddBlacklist} size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {policies?.find(p => p.name === 'blacklist')?.websites?.map((site: string, index: number) => (
                  <Badge key={index} variant="destructive" className="flex items-center gap-1">
                    {site}
                    <button
                      onClick={() => removeFromBlacklist(site)}
                      className="ml-1 hover:text-destructive-foreground"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Browser;